import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import TicketsAPI from '../../api/tickets';

export const state = {
  records: [],
  currentPipelineId: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getTickets(_state) {
    return _state.records;
  },
  getCurrentPipelineId(_state) {
    return _state.currentPipelineId;
  },
  getItemsByStage: _state => stageIdOrKey => {
    return _state.records
      .filter(record => record.current_stage_id === stageIdOrKey)
      .sort((a, b) => a.position - b.position);
  },
  getItemsGroupedByStage(_state) {
    return _state.records.reduce((grouped, item) => {
      const stage = item.current_stage_id;
      if (!grouped[stage]) {
        grouped[stage] = [];
      }
      grouped[stage].push(item);
      return grouped;
    }, {});
  },
};

export const actions = {
  getByPipeline: async function getTicketsByPipeline(
    { commit },
    { pipelineId, params }
  ) {
    commit(types.SET_TICKET_UI_FLAG, { isFetching: true });
    try {
      const response = await TicketsAPI.getByPipeline(pipelineId, params);
      commit(types.SET_TICKETS, response.data.payload);
      commit(types.SET_TICKET_PIPELINE_ID, pipelineId);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isFetching: false });
    }
  },
  show: async function showTicket({ commit, state: _state }, { id }) {
    commit(types.SET_TICKET_UI_FLAG, { isFetching: true });
    try {
      const response = await TicketsAPI.show(id);
      const exists = _state.records.some(r => r.id === response.data.id);
      if (exists) {
        commit(types.EDIT_TICKET, response.data);
      } else {
        commit(types.ADD_TICKET, response.data);
      }
      return response.data;
    } catch {
      return undefined;
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createTicket({ commit }, data) {
    commit(types.SET_TICKET_UI_FLAG, { isCreating: true });
    try {
      const response = await TicketsAPI.create(data);
      commit(types.ADD_TICKET, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { pipelineId, id, ...updateObj }) => {
    commit(types.SET_TICKET_UI_FLAG, { isUpdating: true });
    try {
      const response = await TicketsAPI.update(pipelineId, id, updateObj);
      commit(types.EDIT_TICKET, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, { pipelineId, id }) => {
    commit(types.SET_TICKET_UI_FLAG, { isDeleting: true });
    try {
      await TicketsAPI.destroy(pipelineId, id);
      commit(types.DELETE_TICKET, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isDeleting: false });
    }
  },
  moveStage: async (
    { commit },
    { pipelineId, ticketId, stageId, fromStageId, position }
  ) => {
    commit(types.EDIT_TICKET, {
      id: ticketId,
      current_stage_id: stageId,
      position,
    });
    commit(types.SET_TICKET_UI_FLAG, { isUpdating: true });
    try {
      const response = await TicketsAPI.moveStage(pipelineId, ticketId, {
        stage_id: stageId,
        position,
      });
      commit(types.EDIT_TICKET, response.data);
    } catch (error) {
      commit(types.EDIT_TICKET, {
        id: ticketId,
        current_stage_id: fromStageId,
        position: 0,
      });
      throw new Error(error);
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isUpdating: false });
    }
  },
  changeStatus: async (
    { commit },
    { pipelineId, ticketId, status, closeReasonId }
  ) => {
    commit(types.SET_TICKET_UI_FLAG, { isUpdating: true });
    try {
      const response = await TicketsAPI.changeStatus(pipelineId, ticketId, {
        status,
        close_reason_id: closeReasonId,
      });
      commit(types.EDIT_TICKET, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isUpdating: false });
    }
  },
  assignAgent: async ({ commit }, { pipelineId, ticketId, agentId }) => {
    commit(types.SET_TICKET_UI_FLAG, { isUpdating: true });
    try {
      const response = await TicketsAPI.assignAgent(
        pipelineId,
        ticketId,
        agentId
      );
      commit(types.EDIT_TICKET, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isUpdating: false });
    }
  },
  removeAgent: async ({ commit }, { pipelineId, ticketId, agentId }) => {
    commit(types.SET_TICKET_UI_FLAG, { isUpdating: true });
    try {
      const response = await TicketsAPI.removeAgent(
        pipelineId,
        ticketId,
        agentId
      );
      commit(types.EDIT_TICKET, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_TICKET_UI_FLAG, { isUpdating: false });
    }
  },
  setCurrentPipeline: ({ commit }, pipelineId) => {
    commit(types.SET_TICKET_PIPELINE_ID, pipelineId);
  },
};

export const mutations = {
  [types.SET_TICKET_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_TICKET_PIPELINE_ID](_state, pipelineId) {
    _state.currentPipelineId = pipelineId;
  },
  [types.ADD_TICKET]: MutationHelpers.create,
  [types.SET_TICKETS]: MutationHelpers.set,
  [types.EDIT_TICKET]: MutationHelpers.updateAttributes,
  [types.DELETE_TICKET]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
